/* Artapli slider filters — Library-style controls over Shopify's own filters (collection pages).
 *
 * Anna 2026-10-09: "I do not want the checkboxes, I love the sliders as the Library has."
 *
 * How it works, in one paragraph: snippets/artapli-slider-filters.liquid puts one <ap-slider-filter>
 * block directly before Dawn's own <details> for the same filter. Dawn's checkboxes stay in the form —
 * they are what Dawn serialises into the URL, so nothing about filtering itself changes. This file
 * hides those checkboxes (CSS: `.ap-sf + .js-filter`) and drives them from a slider, a select or
 * chips. After every change it fires a bubbling `input` event, which is exactly what Dawn's
 * FacetFiltersForm listens to: it debounces, builds the URL from the checkboxes, fetches the section,
 * re-renders, and pushes history. A plain reload reads the same URL. If anything here fails, the block
 * marks itself `ap-sf--off` and Dawn's checkboxes come back for that filter.
 *
 * Kill switch: theme setting `artapli_slider_filters`. When it is off, this file is not even loaded.
 */
(() => {
  if (customElements.get('ap-slider-filter')) return;
  document.documentElement.classList.add('ap-sf-ready');

  const CHIP_LIMIT = 12;
  const NUM = /(\d+(?:\.\d+)?)\s*(k)?/i;

  const el = (tag, attrs = {}, text) => {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs)) {
      if (value === false || value == null) continue;
      if (key === 'class') node.className = value;
      else node.setAttribute(key, value === true ? '' : value);
    }
    if (text != null) node.textContent = text;
    return node;
  };

  // "5k–10k" -> "10k", "2–3 colours" -> "3 colours", "under 5k" -> "under 5k"
  const afterDash = (label) => (label.includes('–') ? label.split('–').pop().trim() : label.trim());
  // Short tick text: the number only. "under 5k" -> "5k", "2–3 colours" -> "3", "1 colour" -> "1"
  const tickText = (label) => {
    const match = afterDash(label).match(NUM);
    return match ? match[1] + (match[2] || '') : label;
  };
  const inchToCm = (inches) => {
    const cm = Math.round(Number(inches) * 2.54 * 2) / 2;
    return String(cm).replace(/\.0$/, '');
  };

  class ApSliderFilter extends HTMLElement {
    connectedCallback() {
      if (this.built) return;
      this.built = true;
      try {
        this.build();
      } catch (error) {
        this.off(error);
      }
    }

    off(reason) {
      const block = this.closest('.ap-sf');
      if (block) block.classList.add('ap-sf--off');
      if (reason && window.console) console.warn('[artapli-slider-filters] fallback to checkboxes:', reason);
    }

    t(key) {
      return this.dataset['t' + key.replace(/(^|-)([a-z])/g, (_, __, c) => c.toUpperCase())] || '';
    }

    // Dawn re-renders its <details> innerHTML after every fetch, so the inputs are looked up fresh each time.
    dawnInputs() {
      const block = this.closest('.ap-sf');
      const dawn = block && block.nextElementSibling;
      if (!dawn || !dawn.classList.contains('js-filter')) return [];
      return Array.from(dawn.querySelectorAll('input[type="checkbox"]')).filter(
        (input) => input.name === this.dataset.param
      );
    }

    // Check exactly the given values, uncheck the rest, then hand over to Dawn.
    apply(values, source) {
      const wanted = new Set(values);
      this.dawnInputs().forEach((input) => {
        input.checked = wanted.has(input.value);
      });
      if (source !== 'native') this.dispatchEvent(new Event('input', { bubbles: true }));
    }

    build() {
      const script = this.querySelector('script[data-ap-sf-values]');
      this.values = JSON.parse(script ? script.textContent : '[]');
      // Shopify hands filter_value.value over HTML-escaped (`under 1&quot;`, `Baby &amp; Kids`,
      // `Valentine&#39;s Day`) while Dawn's checkbox values are plain text: decode so they match.
      const decoder = document.createElement('textarea');
      this.values.forEach((value) => {
        decoder.innerHTML = String(value.value);
        value.value = decoder.value;
      });
      const inputs = this.dawnInputs();
      if (!this.values.length || !inputs.length) throw new Error('no values or no Dawn inputs for ' + this.dataset.param);
      // The checkboxes are the truth for what is selected right now.
      const checked = new Set(inputs.filter((input) => input.checked).map((input) => input.value));
      this.values.forEach((value) => (value.active = checked.has(value.value)));

      let kind = this.dataset.kind || 'chips';
      if (kind === 'max' || kind === 'range') {
        const order = (this.dataset.order || '').split('|').filter(Boolean);
        const unknown = this.values.some((value) => !order.includes(value.value));
        if (unknown || this.values.length < 2) kind = 'chips';
        else this.values.sort((a, b) => order.indexOf(a.value) - order.indexOf(b.value));
      }

      this.textContent = '';
      this.classList.add('ap-sf__control', 'ap-sf__control--' + kind);
      const id = 'ApSfInput-' + this.dataset.param.replace(/[^a-z0-9_-]/gi, '-') + '-' + Math.random().toString(36).slice(2, 7);
      const head = el('div', { class: 'ap-sf__head' });
      const label = el(kind === 'chips' ? 'span' : 'label', { class: 'ap-sf__label', for: kind === 'chips' ? null : id, id: id + '-label' }, this.dataset.label);
      this.output = el('output', { class: 'ap-sf__value', for: id, 'aria-live': 'polite' });
      head.append(label, this.output);
      this.append(head);

      if (kind === 'max') this.buildMax(id);
      else if (kind === 'range') this.buildRange(id);
      else if (kind === 'single') this.buildSingle(id);
      else this.buildChips(id);
    }

    /* Max slider (stitches, colours): stop i = buckets 0..i together (OR), last stop = no filter. */
    buildMax(id) {
      const values = this.values;
      const last = values.length - 1;
      const stops = values.slice(0, last).map((value, i) => ({
        readout: i === 0 ? value.label : `${this.t('up-to')} ${afterDash(value.label)}`,
        tick: tickText(value.label),
      }));
      stops.push({ readout: this.t('any-number'), tick: this.t('any') });

      // Current position: the highest selected bucket; nothing selected (or all) = "any".
      const activeIdx = values.map((value, i) => (value.active ? i : -1)).filter((i) => i >= 0);
      let position = activeIdx.length && activeIdx.length < values.length ? Math.max(...activeIdx) : stops.length - 1;
      if (position >= last) position = stops.length - 1;

      const range = el('input', { type: 'range', id, min: 0, max: stops.length - 1, step: 1, value: position, class: 'ap-sf__range' });
      const track = this.track([range]);
      this.append(track, this.ticks(stops.map((stop) => stop.tick)));

      const show = () => {
        const p = Number(range.value);
        this.output.textContent = stops[p].readout;
        range.setAttribute('aria-valuetext', stops[p].readout);
        this.paint(track, 0, p, stops.length - 1, p === stops.length - 1);
      };
      range.addEventListener('input', () => {
        const p = Number(range.value);
        const chosen = p === stops.length - 1 ? [] : values.slice(0, p + 1).map((value) => value.value);
        show();
        this.apply(chosen, 'native');
      });
      show();
    }

    /* Two-thumb range over bucket boundaries (letter height): selects the buckets between the thumbs. */
    buildRange(id) {
      const values = this.values;
      const n = values.length;
      // Boundaries: lower edge of bucket 0, then the upper edge of every bucket. The last one is open.
      const lower = (label) => (label.includes('–') ? label.split('–')[0].replace(/[^\d.]/g, '') : '0');
      const bounds = [lower(values[0].label)].concat(
        values.map((value, i) => (i === n - 1 ? null : afterDash(value.label).replace(/[^\d.]/g, '')))
      );
      const ticks = bounds.map((b, i) => (b === null ? this.t('any') : b));

      const active = values.map((value, i) => (value.active ? i : -1)).filter((i) => i >= 0);
      let lo = 0;
      let hi = n;
      if (active.length && active.length < n) {
        lo = Math.min(...active);
        hi = Math.max(...active) + 1;
      }

      const min = el('input', { type: 'range', id, min: 0, max: n, step: 1, value: lo, class: 'ap-sf__range ap-sf__range--lo', 'aria-label': this.dataset.label + ' min' });
      const max = el('input', { type: 'range', min: 0, max: n, step: 1, value: hi, class: 'ap-sf__range ap-sf__range--hi', 'aria-label': this.dataset.label + ' max' });
      const track = this.track([min, max]);
      this.append(track, this.ticks(ticks));

      const readout = (a, b) => {
        if (a === 0 && b === n) return this.t('any-size');
        const from = bounds[a];
        const to = bounds[b];
        if (b === n) return `${from}" ${this.t('and-up')} · ${inchToCm(from)} cm ${this.t('and-up')}`;
        if (a === 0 && from === '0') return `${this.t('up-to')} ${to}" · ${inchToCm(to)} cm`;
        return `${from}–${to}" · ${inchToCm(from)}–${inchToCm(to)} cm`;
      };
      const show = () => {
        const a = Number(min.value);
        const b = Number(max.value);
        const text = readout(a, b);
        this.output.textContent = text;
        min.setAttribute('aria-valuetext', text);
        max.setAttribute('aria-valuetext', text);
        this.paint(track, a, b, n, a === 0 && b === n);
      };
      const onInput = (moved) => () => {
        let a = Number(min.value);
        let b = Number(max.value);
        // At least one bucket between the thumbs.
        if (b - a < 1) {
          if (moved === min) {
            a = Math.min(a, n - 1);
            b = a + 1;
          } else {
            b = Math.max(b, 1);
            a = b - 1;
          }
          min.value = a;
          max.value = b;
        }
        show();
        const chosen = a === 0 && b === n ? [] : values.slice(a, b).map((value) => value.value);
        this.apply(chosen, 'native');
      };
      min.addEventListener('input', onInput(min));
      max.addEventListener('input', onInput(max));
      show();
    }

    /* Hoop: one choice. "Has a size made for this hoop" (Anna 2026-10-08) — the exact Shopify value. */
    buildSingle(id) {
      const wrap = el('div', { class: 'ap-sf__select' });
      const select = el('select', { id, class: 'ap-sf__select-input' });
      select.append(el('option', { value: '' }, this.t('any-hoop')));
      this.values.forEach((value) => {
        select.append(
          el('option', { value: value.value, selected: value.active, disabled: value.count === 0 && !value.active }, `${value.label} · ${value.count}`)
        );
      });
      wrap.append(select);
      this.append(wrap);
      // The select itself says "Any hoop" or the chosen hoop, so the head readout stays empty.
      const show = () => {
        this.output.textContent = '';
      };
      select.addEventListener('change', () => {
        show();
        this.apply(select.value ? [select.value] : [], 'native');
      });
      // A select fires `input` itself; Dawn only needs one of the two, and both reach the same debounce.
      show();
    }

    /* Chips: multi-select, one chip per Shopify value, counts from Shopify.
       A chip is a label around a nameless checkbox, never a <button>: Dawn's MenuDrawer binds EVERY button in
       the drawer as "close", so a button chip would shut the whole drawer on click. A nameless checkbox is not
       submitted; its bubbling `input` event is what wakes Dawn, after the real checkbox has been set. */
    buildChips(id) {
      this.output.textContent = '';
      // Values with no products under the current filters are left out (Dawn greys them); a filter left with
      // nothing to choose hides itself until the other filters change.
      this.values = this.values.filter((value) => value.count > 0 || value.active);
      if (!this.values.length) {
        this.closest('.ap-sf').classList.add('ap-sf--empty');
        return;
      }
      const list = el('div', { class: 'ap-sf__chips', role: 'group', 'aria-labelledby': id + '-label' });
      const chips = this.values.map((value, i) => {
        const chip = el('label', { class: 'ap-sf__chip' });
        const box = el('input', {
          type: 'checkbox',
          class: 'ap-sf__chip-input',
          id: `${id}-${i}`,
          checked: value.active,
        });
        chip.append(box, el('span', { class: 'ap-sf__chip-label' }, value.label), el('span', { class: 'ap-sf__chip-count' }, String(value.count)));
        box.addEventListener('input', () => {
          value.active = box.checked;
          this.apply(this.values.filter((v) => v.active).map((v) => v.value), 'native');
        });
        list.append(chip);
        return chip;
      });
      this.append(list);

      if (chips.length > CHIP_LIMIT) {
        chips.forEach((chip, i) => {
          if (i >= CHIP_LIMIT && !chip.querySelector('input').checked) chip.classList.add('ap-sf__chip--more');
        });
        list.classList.add('ap-sf__chips--folded');
        // A link-styled toggle, not a <button> (see above).
        const more = el('a', { href: '#', role: 'button', class: 'ap-sf__more', 'aria-expanded': 'false' });
        const label = () => {
          const open = more.getAttribute('aria-expanded') === 'true';
          more.textContent = open ? this.t('show-less') : `${this.t('show-all')} (${chips.length})`;
        };
        more.addEventListener('click', (event) => {
          event.preventDefault();
          const open = more.getAttribute('aria-expanded') !== 'true';
          more.setAttribute('aria-expanded', open ? 'true' : 'false');
          list.classList.toggle('ap-sf__chips--folded', !open);
          label();
        });
        more.addEventListener('keydown', (event) => {
          if (event.key === ' ') { event.preventDefault(); more.click(); }
        });
        label();
        this.append(more);
      }
    }

    track(inputs) {
      const track = el('div', { class: 'ap-sf__track' });
      track.append(el('div', { class: 'ap-sf__rail' }), el('div', { class: 'ap-sf__fill' }), ...inputs);
      return track;
    }

    // Pink fill between the two ends of the selection, like the Library's native range fill.
    paint(track, from, to, steps, isAny) {
      track.style.setProperty('--sf-from', String(from / steps));
      track.style.setProperty('--sf-to', String(to / steps));
      track.classList.toggle('ap-sf__track--any', isAny);
    }

    ticks(labels) {
      const list = el('ol', { class: 'ap-sf__ticks', 'aria-hidden': 'true' });
      list.style.setProperty('--sf-steps', String(labels.length - 1));
      labels.forEach((text, i) => {
        const tick = el('li', {}, text);
        tick.style.setProperty('--sf-i', String(i));
        list.append(tick);
      });
      return list;
    }
  }

  customElements.define('ap-slider-filter', ApSliderFilter);
})();
